import { FormElement } from '@theme/base-form-element'
import firstFocusableElement from '@theme/util-first-focusable-element'
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  size,
} from '@theme/vendor-floating-ui'
import { css, html, nothing, map, createRef, ref } from '@theme/vendor-lit'

const CLASSES_MAPPING = {
  TOGGLE:
    'text-left block w-full border-b border-p-lightest appearance-none text-p-lightest disabled:text-t-border px-sm-forms-padding lg:px-lg-forms-padding pt-sm-forms-padding lg:pt-1 h-sm-forms-inputheight lg:h-lg-forms-inputheight text-body disabled:border-t-border focus:outline-none focus:ring-0 peer group-[[error]]:ring-u-error group-[[error]]:border-[transparent] group-[[error]]:ring-2 focus-visible:ring-2 focus-visible:ring-u-focus',
  TOGGLE_ICON:
    'absolute inset-y-0 flex items-center size-3 pointer-events-none end-sm-forms-padding lg:end-sm-forms-padding text-p-light group-[[error]]:text-u-error h-sm-forms-inputheight lg:h-lg-forms-inputheigh',
  LABEL:
    'absolute caption text-t-foreground-secondary duration-300 transform -translate-y-2.5 scale-75 top-3.5 start-sm-forms-padding lg:start-sm-forms-padding z-10 origin-[0] peer-focus:text-t-foreground peer-disabled:text-t-border peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-2.5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto',
  HELP_TEXT:
    'caption text-t-foreground-secondary start-sm-forms-padding lg:start-sm-forms-padding',
  DROPDOWN:
    'custom-select__dropdown min-w-max bg-c-form-bg max-h-[50vh] overflow-y-scroll overflow-x-clip scrollbar-thin scrollbar-track-[transparent] scrollbar-thumb-p-neutral absolute z-20 hidden top-full left-0 w-full border border-t-border bg-t-background drop-shadow-md rounded-forms-radius group-[[open]]:grid translate-y-4 opacity-0 transition-[opacity,display,transform] [transition-behavior:allow-discrete] motion-reduce:transition-none duration-300 group-[[open]]:translate-y-0 group-[[open]]:opacity-100 !motion-reduce:group-[[open]]:transition-none',
  DROPDOWN_ITEM:
    'px-forms-padding group ring-0 focus:ring-0 appearance-none outline-none py-xs bg-t-background text-body disabled:text-t-disabled text-p-darkest [&[selected]]:text-t-foreground [&[selected]]:bg-t-brand-secondary focus-visible:text-t-foreground hover:text-t-foreground [&:disabled]:hover:text-t-disabled text-left border-b border-t-foreground-secondary last:border-b-0 border-solid',
  SWATCH:
    'block max-w-full aspect-square bg-cover bg-origin-border rounded-full w-5 bg-[--swatch-hex]',
  SWATCH_DISABLED:
    'before:block before:absolute before:left-0 before:right-0 before:w-full before:h-[2px] before:bg-p-darkest before:-rotate-45 before:top-1/2 before:-translate-y-1/2',
}

const CLASSES_MAPPING_BYOB = {
  TOGGLE:
    'text-left flex justify-between items-center rounded-[40px] w-full border-[2px] border-black appearance-none disabled:text-t-border pl-[10px] pr-[15px] py-2 text-body text-[.625rem] font-bold lowercase leading-[150%] disabled:border-t-border focus:outline-none focus:ring-0 peer group-[[error]]:ring-u-error group-[[error]]:border-[transparent] group-[[error]]:ring-2 focus-visible:ring-2 focus-visible:ring-u-focus',
  TOGGLE_ICON:
    'flex items-center size-3 pointer-events-none group-[[open]]:z-30 group-[[open]]:rotate-180 transition-transform transition-200 group-[[error]]:text-u-error',
  LABEL: 'sr-only',
  HELP_TEXT: 'caption text-t-foreground-secondary px-sm',
  DROPDOWN:
    'custom-select__dropdown bg-t-background z-20 absolute border border-[2px] border-black rounded-[18px] max-h-[--dropdown-option-height] lg: overflow-y-scroll overflow-x-clip scrollbar-thin scrollbar-track-[transparent] scrollbar-thumb-[#4B4B4B] z-20 hidden top-full left-0 w-full group-[[open]]:grid translate-y-4 opacity-0 transition-[opacity,display,transform] [transition-behavior:allow-discrete] motion-reduce:transition-none duration-300 group-[[open]]:translate-y-0 group-[[open]]:opacity-100 !motion-reduce:group-[[open]]:transition-none lg:max-w-[296px]',
  DROPDOWN_ITEM:
    'group ring-0 focus:ring-0 appearance-none outline-none px-[10px] py-2 text-body disabled:text-t-disabled text-p-darkest [&[selected]]:bg-[#eee] enabled:focus-visible:bg-[#eee] enabled:hover:bg-[#eee] text-left rounded-[40px] lowercase flex justify-between items-center',
  SWATCH:
    'block max-w-full aspect-square bg-cover bg-origin-border rounded-full w-5 bg-[--swatch-hex]',
  SWATCH_DISABLED:
    'before:block before:absolute before:left-0 before:right-0 before:w-full before:h-[2px] before:bg-p-darkest before:-rotate-45 before:top-1/2 before:-translate-y-1/2',
}

export class CustomSelect extends FormElement {
  static get properties() {
    return {
      label: { type: String },
      placeholder: { type: String, attribute: 'placeholder' },
      alwaysShowPlaceholder: {
        type: Boolean,
        attribute: 'always-show-placeholder',
      },
      error: { type: Boolean },
      _options: { type: Array, attribute: 'options' },
      helpText: { type: String },
      errorText: { type: String },
      selectToggleIcon: { type: String, attribute: 'select-toggle-icon' },
      selectDropdownClass: { type: String, attribute: 'select-dropdown-class' },
      selectDropdownItemClass: {
        type: String,
        attribute: 'select-dropdown-item-class',
      },
      labelClass: { type: String, attribute: 'label-class' },
      selectToggleIconClass: {
        type: String,
        attribute: 'select-toggle-icon-class',
      },
      selectToggleClass: { type: String, attribute: 'select-toggle-class' },
      selectToggleIconSize: {
        type: String,
        attribute: 'select-toggle-icon-size',
      },
      selectToggleLabelClass: {
        type: String,
        attribute: 'select-toggle-label-class',
      },
      newCustomSelect: { type: Boolean, attribute: 'new-custom-select' },
      _expanded: { state: true },
    }
  }

  static EVENTS = {
    OPEN: 'open',
    CLOSE: 'close',
  }

  static styles = [
    css`
      @starting-style {
        .custom-select__dropdown {
          opacity: 0 !important;
          transform: translateY(2rem) !important;
        }
      }
    `,
  ]

  get CLASSES_TO_USE() {
    return this.newCustomSelect ? CLASSES_MAPPING_BYOB : CLASSES_MAPPING
  }

  constructor() {
    super()

    this.label = ''
    this.placeholder = ''
    this.alwaysShowPlaceholder = false
    this.error = false
    this._options = []
    this.helpText = ''
    this.errorText = ''
    this.selectToggleIcon = ''
    this.selectDropdownClass = ''
    this.selectDropdownItemClass = ''
    this.labelClass = ''
    this.selectToggleIconClass = ''
    this.selectToggleClass = ''
    this.selectToggleIconSize = ''
    this.selectToggleLabelClass = ''
    this.newCustomSelect = false
    this._expanded = false
    this.toggleElement = createRef()
    this.listboxElement = createRef()
    this._cleanupWatcher = null
    this._mutationObserver = null
    this._handleDocumentClick = this._handleDocumentClick.bind(this)
    this._handleDocumentKeyDown = this._handleDocumentKeyDown.bind(this)
    this._handleClick = this._handleClick.bind(this)
    this._collectionDataFromChildren()
  }

  connectedCallback() {
    super.connectedCallback()

    this._mutationObserver = new MutationObserver(() => {
      this._collectionDataFromChildren()
      this.requestUpdate()
    })
    this._mutationObserver.observe(this, {
      subtree: true,
      attributes: true,
      childList: true,
      attributeFilter: [
        'disabled',
        'selected',
        'data-swatch',
        'data-swatch-hex',
        'data-short-description',
        'data-shade-description',
        'data-type',
        'data-image',
        'data-variant-available',
        'data-variant-inventory',
        'value',
      ],
    })
  }

  _collectionDataFromChildren() {
    const $options = Array.from(this.querySelectorAll('option'))

    if ($options.length === 0) return
    this._options = $options.map((option) => ({
      label: option.textContent || '',
      value: option.value || '',
      disabled: option.disabled || false,
      selected: option.selected || false,
      swatch: option.dataset.swatch || '',
      swatchHex: option.dataset.swatchHex || '',
      shortDescription: option.dataset.shortDescription || '',
      shadeDescription: option.dataset.shadeDescription || '',
      type: option.dataset.type || '',
      image: option.dataset.image || '',
      quantity: option.dataset.quantity ? parseInt(option.dataset.quantity) : 0,
    }))
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties)
    document.addEventListener('click', this._handleDocumentClick)
    document.addEventListener('keydown', this._handleDocumentKeyDown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._mutationObserver) {
      this._mutationObserver.disconnect()
      this._mutationObserver = null
    }
    document.removeEventListener('click', this._handleDocumentClick)
    document.removeEventListener('keydown', this._handleDocumentKeyDown)
  }

  _handleDocumentClick(event) {
    // If event.composedPath() is available — it will show nodes through the Shadow DOM
    const path = event.composedPath ? event.composedPath() : [event.target]

    // The first "real" node in the path (ignoring window/document)
    const firstNode = path.find(
      (node) => node && node !== window && node !== document
    )

    if (!this.contains(event.target) && firstNode.role !== 'combobox') {
      this._expanded = false
      return
    }
  }

  _handleClick(event) {
    if (!this._expanded) return
    const listbox = this.listboxElement.value
    const target = event.target
    if (!listbox || !target) return
    if (!listbox.contains(target)) return

    const selectedItem =
      target.tagName === 'BUTTON' ? target : target.closest('button')

    if (!selectedItem) return
    if (selectedItem.disabled) return
    this.selectValue(selectedItem.value)

    if (!this.multiple) {
      this._expanded = false
    }
  }

  // Check if multiple is true, if so, add or remove the value from the array
  // If multiple is false, set the value to the selected value
  selectValue(value) {
    if (this.multiple) {
      const selectedValues = new Set(this.parsedValue)
      if (selectedValues.has(value)) {
        selectedValues.delete(value)
      } else {
        selectedValues.add(value)
      }

      this.value = Array.from(selectedValues).filter(Boolean).join(',')
    } else {
      this.value = value
    }

    const innerSelect = this.querySelector('select')
    if (innerSelect) {
      innerSelect.value = value
    }
  }

  get currentActiveElement() {
    if (this.renderRoot instanceof ShadowRoot) {
      return this.renderRoot.activeElement
    }
    return document.activeElement
  }

  _handleDocumentKeyDown(event) {
    if (!this._expanded) return
    if (event.key === 'Escape') {
      this._expanded = false
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      const listbox = this.listboxElement.value
      if (!listbox) return
      const items = Array.from(listbox.querySelectorAll('button'))
      const activeElement = this.currentActiveElement
      const currentIndex = items.findIndex((item) => item === activeElement)

      if (currentIndex === -1) {
        items[0].focus()
        return
      }

      const nextIndex =
        event.key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1
      if (nextIndex < 0) {
        return items[items.length - 1].focus()
      } else if (nextIndex >= items.length) {
        return items[0].focus()
      } else {
        items[nextIndex].focus()
      }
    }
  }

  _handleToggleClick() {
    this._expanded = !this._expanded
  }

  updated(_changedProperties) {
    super.updated(_changedProperties)
    if (
      _changedProperties.has('_expanded') &&
      typeof _changedProperties.get('_expanded') === 'boolean'
    ) {
      this._handleExpandedStateChange()
    }
  }

  _positionListbox(toggleElement, listboxElement) {
    const elementId = this.id
    computePosition(toggleElement, listboxElement, {
      placement: 'bottom',
      strategy: 'absolute',
      middleware: [
        offset(this.newCustomSelect ? 0 : 5),
        flip({
          crossAxis: false,
          fallbackPlacements: ['top', 'bottom'],
          fallbackAxisSideDirection: 'start',
        }),
        size({
          apply({ elements }) {
            if (elementId.indexOf('gwp-parent-') > -1) {
              const cardContainer = document.querySelector(
                '[data-gwp-carousel]'
              )
              if (cardContainer) {
                const cardRect = cardContainer.getBoundingClientRect()
                const width = cardRect.width

                // Change styles, e.g.
                Object.assign(elements.floating.style, {
                  minWidth: `${Math.max(0, width)}px`,
                  maxWidth: `${Math.max(0, width)}px`,
                })
              }
            }
          },
        }),
      ],
    }).then(({ x, y, placement }) => {
      // in the new custom select, the dropdown actually sits on top of (z-index) the toggle component, rather than above/below (y-axis)
      // 38 is the height of the toggle
      const calculatedY = this.newCustomSelect
        ? placement === 'top'
          ? y + toggleElement.offsetHeight
          : y - toggleElement.offsetHeight
        : y

      let calculatedX = x
      // positioning of gwp custom select is different
      if (elementId.indexOf('gwp-parent-') > -1) {
        calculatedX = -17
        if (window.matchMedia('(min-width: 425px)').matches) {
          calculatedX = -137
        }
      }

      const calculatdX = calculatedX

      Object.assign(listboxElement.style, {
        left: `${calculatdX}px`,
        top: `${calculatedY}px`,
      })
    })
  }

  async _handleExpandedStateChange() {
    this._cleanupWatcher?.()
    await this.updateComplete
    if (!this._expanded) {
      return
    }
    const toggleElement = this.toggleElement.value
    const listboxElement = this.listboxElement.value

    const options = this.listboxElement.value?.querySelectorAll('button')
    if (!options || options.length === 0) return
    const buttonHeights = Array.from(options)
      .slice(0, 8)
      .reduce((acc, option) => {
        return acc + option.getBoundingClientRect().height
      }, 0)

    this.style.setProperty('--dropdown-option-height', `${buttonHeights}px`)

    if (!toggleElement || !listboxElement) return
    // Leveraging the floating-ui library to position the listbox
    // and update the position when the window is resized
    // or the listbox is scrolled
    // Anchoring Logic allows the listbox to be positioned relative to the toggle element
    // depending on the available space in the viewport
    this._cleanupWatcher = autoUpdate(toggleElement, listboxElement, () =>
      this._positionListbox(toggleElement, listboxElement)
    )

    this._onExpanded()
  }

  _onCollapsed() {
    this.toggleElement.value?.focus()
    this.$emit(CustomSelect.EVENTS.CLOSE, { value: this.value })
  }

  _onExpanded() {
    setTimeout(() => {
      this.listboxElement.value &&
        firstFocusableElement(this.listboxElement.value)?.focus()
    }, 100)

    this.$emit(CustomSelect.EVENTS.OPEN, { value: this.value })
  }

  get _processedOptions() {
    const options = this._options
    const value = this.parsedValue

    return options.map((option) => {
      return {
        ...option,
        selected:
          this.multiple && Array.isArray(value)
            ? value.includes(option.value)
            : option.value === value,
      }
    })
  }

  get parsedValue() {
    return this.multiple ? String(this.value).split(',') : this.value
  }

  get idForToggle() {
    return `${this.id}-toggle`
  }

  get idForListbox() {
    return `${this.id}-listbox`
  }

  get helpTextHTML() {
    if (!this.helpText) return nothing

    return html`
      <p id="${this.id}_text" class=${this.CLASSES_TO_USE.HELP_TEXT}>
        ${this.helpText}
      </p>
    `
  }

  get labelHTML() {
    return html`
      <label
        for="${this.idForToggle}"
        class=${this.labelClass ? this.labelClass : this.CLASSES_TO_USE.LABEL}
      >
        ${this.label}
      </label>
    `
  }

  getDropdownItemHTML(option, index) {
    const isLast = index === this._options.length - 1
    return html`
      <button
        type="button"
        class="${
          this.selectDropdownItemClass
            ? this.selectDropdownItemClass
            : this.CLASSES_TO_USE.DROPDOWN_ITEM
        }"
        role="option"
        aria-selected="${option.selected}"
        value="${option.value}"
        ?selected=${option.selected}
        ?disabled=${option.disabled}
      >
        <span
          class="${
            this.newCustomSelect ? '' : 'justify-center'
          } text-body flex w-full items-center gap-2 text-[.625rem] font-bold lowercase leading-[150%]"
        >
          ${
            option.swatch || option.swatchHex
              ? html`<span
                  class="${
                    option.disabled ? this.CLASSES_TO_USE.SWATCH_DISABLED : ''
                  } ${
                    option.swatch ? '' : this.CLASSES_TO_USE.SWATCH
                  } relative rounded-full border border-solid border-t-border"
                  style="--swatch-hex: ${option.swatchHex};"
                  >${
                    option.swatch
                      ? html`<img
                          class="${this.CLASSES_TO_USE.SWATCH}"
                          src=${option.swatch}
                        />`
                      : ''
                  }</span
                >`
              : ''
          }
          <span class="flex w-full justify-between">
            <span class="max-w-[calc(100%-12px)] whitespace-nowrap"
              >${option.label}${
                option.shadeDescription ? ` - ${option.shadeDescription}` : ''
              }</span
            >
            <span>${option.disabled ? ' out of stock' : ''}</span>
          </span>
        </span>
        ${
          isLast
            ? html`<svg-icon
                class="pointer-events-none flex size-3 rotate-180 items-center"
                src="icon-caret"
              ></svg-icon>`
            : ''
        }
      </button>
    `
  }

  get hasSelectedOption() {
    return this._processedOptions.some((option) => option.selected)
  }

  buildLabelWithSwatch(option) {
    if (!option.swatch && !option.swatchHex) return option.label

    return html`
      <span
        class="${
          option.disabled ? 'text-[#0000004d]' : ''
        } flex items-center gap-2 text-[10px] font-bold lowercase leading-[150%] tracking-[0.5px]"
        style="--swatch-hex: ${option.swatchHex};"
      >
        ${
          option.swatch || option.swatchHex
            ? html`<span
                class="${
                  option.disabled ? this.CLASSES_TO_USE.SWATCH_DISABLED : ''
                } ${
                  option.swatch ? '' : this.CLASSES_TO_USE.SWATCH
                } relative rounded-full border border-solid border-t-border"
                style="--swatch-hex: ${option.swatchHex};"
                >${
                  option.swatch
                    ? html`<img
                        class="${this.CLASSES_TO_USE.SWATCH}"
                        src=${option.swatch}
                      />`
                    : ''
                }</span
              >`
            : ''
        }
        ${
          this.id.indexOf('gwp-parent-') > -1
            ? html`&nbsp;`
            : html`
                ${option.label}${
                  option.shadeDescription ? ` - ${option.shadeDescription}` : ''
                }
              `
        }
      </span>
    `
  }

  get _currentToggleLabel() {
    if (this.alwaysShowPlaceholder || !this.hasSelectedOption) {
      return this.placeholder
    }

    const processedOptions = this._processedOptions

    if (this.multiple) {
      const selectedOptions = processedOptions.filter(
        (option) => option.selected
      )
      return selectedOptions.map((option) => option.label).join(', ')
    }

    const selectedOption = processedOptions.find((option) => option.selected)

    return selectedOption?.swatch || selectedOption?.swatchHex
      ? this.buildLabelWithSwatch(selectedOption)
      : selectedOption
        ? selectedOption.label
        : this.placeholder
  }

  get getCustomSelectHTML() {
    return html`
      <div
        class=${`group peer relative h-full w-full${this.newCustomSelect ? ' max-w-none lg:max-w-[296px]' : ''}`}
        ?error=${this.error}
        ?open=${this._expanded}
        @click=${this._handleClick}
      >
        <button
          ${ref(this.toggleElement)}
          type="button"
          role="combobox"
          @click=${this._handleToggleClick}
          aria-haspopup="listbox"
          class="${
            this.selectToggleClass
              ? this.selectToggleClass
              : this.CLASSES_TO_USE.TOGGLE
          } relative"
          id="${this.idForToggle}"
          value="${this.value}"
          aria-controls="${this.idForListbox}"
          aria-expanded="${this._expanded}"
          aria-disabled="${this.disabled}"
          ?disabled=${this.disabled}
          ${this.helpText ? `aria-describedby="${this.id}_text"` : ''}
        >
          <span class="${this.selectToggleLabelClass} block truncate pr-sm"
            >${this._currentToggleLabel}</span
          >
          <div
            class="${
              this.selectToggleIconClass
                ? this.selectToggleIconClass
                : this.CLASSES_TO_USE.TOGGLE_ICON
            }"
          >
            <svg-icon
              src="${this.selectToggleIcon || 'icon-select-arrows'}"
              class="text-inherit"
              style="font-size: ${
                this.selectToggleIconSize
                  ? `${this.selectToggleIconSize}px`
                  : '12px'
              }"
            ></svg-icon>
          </div>
        </button>

        <div
          ${ref(this.listboxElement)}
          id="dropdown"
          role="listbox"
          id="${this.idForListbox}"
          ?inert=${!this._expanded}
          class="${
            this.selectDropdownClass
              ? this.selectDropdownClass
              : this.CLASSES_TO_USE.DROPDOWN
          }"
          ${this.multiple ? 'aria-multiselectable="true"' : ''}
        >
          ${map(this._processedOptions, (option, index) => this.getDropdownItemHTML(option, index))}
        </div>
        ${this.labelHTML}
      </div>
      ${this.helpTextHTML}
    `
  }

  render() {
    // If there are no options, do not render anything
    if (!Array.isArray(this._options) || this._options.length === 0) {
      return html`${nothing}`
    }
    this.classList.remove('opacity-0')

    return this.getCustomSelectHTML
  }
}

if (!customElements.get('custom-select')) {
  customElements.define('custom-select', CustomSelect)
}
